import { Button } from "@chakra-ui/react";
import { Table } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import type { ZoneActivityLabel } from "../types/ActivityLabel";
import { useCreateActivityLabel } from "../useCases/useCreateActivityLabel";
import { useUpdateActivityLabel } from "../useCases/useUpdateActivityLabel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { LuPlus } from "react-icons/lu";
import { Pagination } from "@/shared/core-configs";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { ActivityLabelsTableColumns } from "../Columns";
import { useAuth } from "@/shared/session";
import {
  ActivityLabelFormInput,
  CreateActivityLabelModal,
} from "./CreateActivityLabelModal";
import { UpdateActivityLabelModal } from "./UpdateActivityLabelModal";
import {
  ActivityLabel,
  useGetActivityLabels,
} from "@/shared/core-activity-labels";
import { toast } from "@/ui/chakra/toaster";
import { PageContainer } from "@/ui/containers/PageContainer";

export const ActivityLabelsTable = () => {
  const { session } = useAuth();
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });
  const [showDisabled, setShowDisabled] = useState(false);
  const [selectedActivityLabel, setSelectedActivityLabel] =
    useState<ZoneActivityLabel | null>(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {
    data: activityLabels = [],
    refetch,
    isPending,
    pagination: meta,
  } = useGetActivityLabels({
    page: pagination.page,
    pageSize: pagination.pageSize,
    showDisabled,
    searchTerm: debouncedSearchTerm,
    ...order,
    zoneIds: session?.zone ?? undefined,
  });
  const { mutate: createActivityLabel } = useCreateActivityLabel();
  const { mutate: updateActivityLabel } = useUpdateActivityLabel();

  const handleOpenCreateModal = () => setCreateModalOpen(true);
  const handleCloseCreateModal = () => setCreateModalOpen(false);

  const handleOpenEditModal = (activityLabel: ZoneActivityLabel) => {
    setSelectedActivityLabel(activityLabel);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedActivityLabel(null);
  };

  const handleCreateActivityLabel = (data: ActivityLabelFormInput) => {
    createActivityLabel(data, {
      onSuccess: () => {
        setCreateModalOpen(false);
        notify.success("Etiqueta creada");
        refetch();
      },
      onError: (error) => notify.error("No se pudo crear la etiqueta", error),
    });
  };

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = useCallback(
    async (activityLabel: ActivityLabel) => {
      const ok = await confirmToggle(
        activityLabel.enabled,
        `la etiqueta "${activityLabel.label}"`,
      );
      if (!ok) return;
      const updatedActivityLabel: ActivityLabelFormInput = {
        label: activityLabel.label,
        zonesIds: activityLabel.zones?.map((zone) => zone.id) ?? [],
        enabled: !activityLabel.enabled,
      };
      updateActivityLabel(
        { id: activityLabel.id ?? "", updatedActivityLabel },
        {
          onSuccess: () => {
            notify.success(
              activityLabel.enabled
                ? "Etiqueta deshabilitada"
                : "Etiqueta habilitada",
            );
            refetch();
          },
          onError: (error) =>
            notify.error("No se pudo actualizar la etiqueta", error),
        },
      );
    },
    [updateActivityLabel, refetch, confirmToggle],
  );

  const activityLabelsTableColumns = useMemo(
    () => ActivityLabelsTableColumns(handleToggleEnabled),
    [handleToggleEnabled],
  );

  const handleEditActivityLabel = (item: ZoneActivityLabel) => {
    if (item.zones && item.zones.length > 1) {
      toast({
        title: "Error al editar etiqueta de actividad",
        description: "No se puede editar",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    handleOpenEditModal(item);
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm]);

  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Etiquetas de actividad"
      description="Crea y administra las etiquetas de actividad según las necesidades."
      actions={
        <Button colorPalette="brand" onClick={handleOpenCreateModal}>
          <LuPlus />
          Crear etiqueta de actividad
        </Button>
      }
    >
      <Table
        searchPlaceholder="Buscar por nombre de etiqueta…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        emptyDescription="Las etiquetas clasifican la actividad de cada vale."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleOpenCreateModal}
          >
            Crear etiqueta de actividad
          </Button>
        }
        headers={activityLabelsTableColumns}
        data={activityLabels}
        extractItemKey={(item: ZoneActivityLabel) => item.id}
        onEdit={(item: ZoneActivityLabel) => {
          handleEditActivityLabel(item);
        }}
        pages={meta?.totalPages}
        totalItems={meta?.count}
        itemsLabel="etiquetas"
        showDisabledCheck
        onSortChange={(sort, order) => {
          setOrder({ sort, order });
        }}
        onShowDisabledChange={(showDisabled) => setShowDisabled(showDisabled)}
        onSearch={(searchTerm) => setSearchTerm(searchTerm)}
        onChangePage={(page) =>
          setPagination({ ...pagination, page: page + 1 })
        }
        hiddenColumnsKey="activityLabels"
        isLoading={isPending}
      />
      <CreateActivityLabelModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateActivityLabel}
      />
      {selectedActivityLabel && (
        <UpdateActivityLabelModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          selectedActivityLabel={selectedActivityLabel}
        />
      )}
    </PageContainer>
  );
};
